import React, { useState, useEffect, useRef } from 'react';
import { Product } from '../types';

interface ProductSearchSelectProps {
    products: Product[];
    selectedProduct: number | null;
    onProductSelect: (productId: number | null) => void;
    placeholder?: string;
    className?: string;
}

const ProductSearchSelect: React.FC<ProductSearchSelectProps> = ({
    products,
    selectedProduct,
    onProductSelect,
    placeholder = "Rechercher et sélectionner un produit...",
    className = ""
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    // Filter products based on search term
    const filteredProducts = products.filter(product => {
        const matchesSearch = !searchTerm ||
            product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.code?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Get selected product info
    const selectedProductInfo = selectedProduct
        ? products.find(p => p.id === selectedProduct)
        : null;

    const handleProductSelect = (product: Product) => {
        onProductSelect(product.id);
        setSearchTerm('');
        setIsOpen(false);
    };

    const handleClear = () => {
        onProductSelect(null);
        setSearchTerm('');
        setIsOpen(false);
    };

    const displayValue = selectedProductInfo
        ? `${selectedProductInfo.code} - ${selectedProductInfo.designation || selectedProductInfo.name} (${formatPrice(selectedProductInfo.unit_price || selectedProductInfo.price || 0)})`
        : searchTerm;

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={displayValue}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!e.target.value && selectedProduct) {
                            onProductSelect(null);
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    className="w-full px-4 py-2 pr-10 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                />
                {selectedProduct && (
                    <button
                        onClick={handleClear}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-eldai-gray hover:text-eldai-blue"
                    >
                        ✕
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-eldai-gray rounded-lg shadow-xl max-h-64 overflow-y-auto product-search-item-list">
                    {filteredProducts.length > 0 ? (
                        <>
                            <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs text-gray-500 border-b border-gray-200">
                                {filteredProducts.length} produit(s) trouvé(s)
                            </div>
                            {filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    onClick={() => handleProductSelect(product)}
                                    className="px-4 py-3 hover:bg-eldai-yellow/20 hover:border-l-4 hover:border-l-eldai-yellow cursor-pointer border-b border-eldai-gray-light last:border-b-0 transition-all duration-200 transform hover:scale-[1.01]"
                                >
                                    <div className="font-semibold text-eldai-blue">
                                        {product.code} - {product.designation || product.name}
                                    </div>
                                    <div className="text-sm text-eldai-gray">
                                        {formatPrice(product.unit_price || product.price || 0)}
                                        {!product.is_available && (
                                            <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">
                                                Indisponible
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className="px-4 py-2 text-eldai-gray text-center">
                            Aucun produit trouvé
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductSearchSelect;
